import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export type MarketDataProvider = "md1" | "md2" | "auto";

export interface CustomerSettings {
  customer_id: string;
  operator_emails: string[];
  inbox_watcher_enabled: boolean;
  telegram_allow_from: string[];
  market_data_provider: MarketDataProvider;
  mail_reply_targets: string[];
  /** Offer family agents (a child's helper) in this workspace. */
  family_agents_enabled: boolean;
  /** Show the Todos tab in this workspace. */
  todos_enabled: boolean;
  /** Offer subagent blueprints (making one) in this workspace. Existing
   *  blueprints keep working either way. */
  subagents_enabled: boolean;
  /** Offer workflows (pipelines of subagent stages lattice runs). Needs
   *  subagents on; existing workflows stay listed either way. */
  workflows_enabled: boolean;
  // Each subagent's commands in a sandbox of its own (services/subagent_sandbox.py).
  subagent_sandbox_enabled: boolean;
  jobs_enabled: boolean;
  /** The Tasks page, the agents' task command and Lattice running tasks. */
  tasks_enabled: boolean;
  /** Agents get the one TASKS rule and OpenClaw's own background work is off (needs tasks_enabled). */
  tasks_only: boolean;
}

/** The customer-settings flags a nav item (or a page) can be gated on.
 *  `machines_enabled` and `connections_enabled` are not in this response:
 *  they come from the machines switch (GET …/machines/switch) and the
 *  connections list (GET …/connections) and are merged in by useSettingsFlags. */
export type CustomerSettingsFlag =
  | "todos_enabled"
  | "subagents_enabled"
  | "workflows_enabled"
  | "jobs_enabled"
  | "tasks_enabled"
  | "machines_enabled"
  | "connections_enabled"
  // Read-only in the settings answer (stream G); Settings › Outside access
  // shows when any of the three is on, Code › Apps by apps_enabled.
  | "apps_enabled"
  | "deployments_enabled"
  | "booking_pages_enabled";

export interface CustomerSettingsPatch {
  operator_emails?: string[];
  inbox_watcher_enabled?: boolean;
  telegram_allow_from?: string[];
  market_data_provider?: MarketDataProvider;
  mail_reply_targets?: string[];
  family_agents_enabled?: boolean;
  todos_enabled?: boolean;
  subagents_enabled?: boolean;
  workflows_enabled?: boolean;
  subagent_sandbox_enabled?: boolean;
  jobs_enabled?: boolean;
  tasks_enabled?: boolean;
  tasks_only?: boolean;
}

/** The settings read as query options, shared by the hook and the boot
 *  prefetch (App.tsx), which starts it together with /api/auth/me. */
export function customerSettingsQuery(customerId: string | null) {
  return queryOptions({
    queryKey: ["customer-settings", customerId],
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CustomerSettings>(`/api/customers/${customerId}/settings`, {
        signal,
      }),
    staleTime: 5_000,
  });
}

export function useCustomerSettings(customerId: string | null) {
  return useQuery(customerSettingsQuery(customerId));
}

// Save = sync. Backend's PUT writes state.json + manifest, then
// reconciles the gateway and (if inbox_watcher_enabled changed) flips
// the cron registrations in lockstep before returning. The response
// reflects the live state, so the UI doesn't need to refetch.
export function useSaveCustomerSettings() {
  const qc = useQueryClient();
  return useMutation({
    // The section's Save bar shows the error itself.
    meta: { errorHandled: true },
    mutationFn: async (params: { customerId: string; patch: CustomerSettingsPatch }) =>
      apiFetch<CustomerSettings>(`/api/customers/${params.customerId}/settings`, {
        method: "PUT",
        body: params.patch,
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData(["customer-settings", vars.customerId], data);
    },
  });
}
